<template>
  <div class="flex w-full" style="position: relative">
    <div v-if="gliders.length" class="d-flex flex-col w-full h-full">
      <div class="flex-grow-0 w-full">
        <v-tabs v-if="showTabs" v-model="selectedGliderIdx" align-with-title>
          <v-tab>
            <v-icon class="mr-2 !text-black">{{ fleetIco }}</v-icon>
            {{ fleet.FLE_LIBELLE }}
          </v-tab>
          <v-tab v-for="(glider, idx) in gliders" :key="glider.id" :title="glider.libelle">
            {{ glider.libelle }}
          </v-tab>
        </v-tabs>
      </div>

      <v-layout column fill-height v-if="selectedGliderIdx == 0">
        <div style="flex: 1 0 auto; position: relative">
          <v-card class="maincontent logbook mb-1">
            <v-card-title style="flex-direction: column; align-items: stretch; height: 100%; overflow: auto">
              <v-layout row wrap style="flex: 0 0 auto">
                <v-flex class="centercontent" xs1>
                  <span style="font-weight: bold">{{ $t('Pilot.LogBook.Date') }}</span>
                </v-flex>
                <v-flex class="centercontent" xs1>
                  <span style="font-weight: bold">{{ $t('Pilot.LogBook.Time') }}</span>
                </v-flex>
                <v-flex class="centercontent" xs3>
                  <span style="font-weight: bold">{{ $t('Pilot.LogBook.From') }}</span>
                </v-flex>
                <v-flex class="centercontent" xs7>
                  <span style="font-weight: bold">{{ $t('Pilot.LogBook.Message') }}</span>
                  <v-spacer />
                  <v-btn small @click="downloadFile(`/api/logbook/csv/?fleid=${fleet.FLE_ID}`)" color="primary">
                    {{ $t('Pilot.LogBook.Download') }}
                    <v-icon small class="pl-2">mdi-download</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
              <v-layout ref="logbook" row wrap style="flex: 1 0; overflow: auto; align-items: flex-start">
                <v-flex xs12 class="inline-block">
                  <mission-detail-log-book-row
                    hide-cycle
                    :rowItem="message"
                    v-for="message in fleetState.logBookItems"
                    :key="message.lbkId"
                  />
                </v-flex>
              </v-layout>
            </v-card-title>
          </v-card>
        </div>
        <v-layout row wrap style="flex: 0 0 auto">
          <v-flex xs10 px-2>
            <v-textarea
              :rows="2"
              :label="$t('Pilot.LogBook.Message')"
              @keydown.enter.exact.prevent="sendMessage()"
              v-model="message"
            />
          </v-flex>
          <v-flex xs2 pr-3>
            <v-btn color="primary" style="width: 100%" @click="sendMessage()">{{ $t('Pilot.LogBook.Send') }}</v-btn>
            <!-- <v-switch hide-details v-model="lbkAlertMission" @change="saveAlertMission" :label="$t('Pilot.LogBook.AlertMission')" v-if="mission.misActifOn"></v-switch> -->
          </v-flex>
        </v-layout>
      </v-layout>
      <mission-detail-log-book
        v-else
        :readonly="
          props.fleet.activeMissionIds.every(
            misId => misId != store.myGetters.missionsById[gliders[selectedGliderIdx - 1].id].misId,
          )
        "
        :uiid="uiid"
        :isleft="isleft"
        :mission="store.myGetters.missionsById[gliders[selectedGliderIdx - 1].id]"
        :isVisible="props.isVisible"
        :key="selectedGliderIdx"
      />
    </div>
    <div v-else class="w-full h-full flex flex-col mx-4 items-center justify-center title gap-2">
      <span>{{ $t('Fleet.NoGliders') }}</span>
    </div>
  </div>
</template>
<script setup lang="ts">
import apiHelpers from '@/utils/api'
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import store from '@/store'
import { AloDbFleet, isAloDbFleetCoverage, isAloDbFleetFormation } from '@/models/alodb/AloDbFleet'
import MissionDetailLogBook from '@/components/mission/MissionDetailLogBook.vue'
import MissionDetailLogBookRow from '@/components/mission/MissionDetailLogBookRow.vue'
import { buildFleetUIStateId } from '@/models/ui/FleetUiState'

const props = defineProps<{
  fleet: AloDbFleet
  isleft: boolean
  uiid: string
  isVisible: boolean
}>()

const missions = computed(
  () =>
    props.fleet.param?.missionIds
      ?.map(id => store.myGetters?.missionsById[id])
      ?.filter(m => !!m)
      ?.sort((m, m1) =>
        !props.fleet.activeMissionIds.includes(m.misId!)
          ? 1
          : !props.fleet.activeMissionIds.includes(m1.misId!)
          ? -1
          : props.fleet.activeMissionIds.findIndex(misId => m.misId == misId) -
            props.fleet.activeMissionIds.findIndex(misId => m1.misId == misId),
      ) ?? [],
)

const gliders = computed(() => missions.value.map(m => ({ id: m.misId, libelle: m.gliKey })))

const selectedGliderIdx = ref(0)

const message = ref('')

const logbook = ref()

const fleet_uiid = computed(() => {
  return buildFleetUIStateId(props.fleet.FLE_ID!, 'detail')
})

// recupere le state de travail
const fleetState = computed(() => {
  // retourne un FleetUiState
  return store.myGetters.fleetUiStateByUiid(fleet_uiid.value)
})

const fleetIco = computed(() => {
  return isAloDbFleetCoverage(props.fleet)
    ? 'mdi-select-group'
    : isAloDbFleetFormation(props.fleet)
    ? 'mdi-vector-circle'
    : 'mdi-multicast'
})

const showTabs = ref(true)
onMounted(() => {
  setTimeout(() => {
    showTabs.value = false
    nextTick(() => {
      showTabs.value = true
    })
  }, 100)
})

function downloadFile(url: string) {
  apiHelpers.downloadFile(url)
}

function sendMessage() {
  store.myActions.UI_FLEET_LOGBOOK_SEND_MESSAGE_REQUEST({ fleId: props.fleet.FLE_ID!, message: message.value.trim() })
  message.value = ''
}

if (props.fleet.FLE_ID) store.myActions.UI_FLEET_LOGBOOK_REQUEST(props.fleet.FLE_ID)

watch(
  () => props.fleet.FLE_ID,
  () => {
    store.myActions.UI_FLEET_LOGBOOK_REQUEST(props.fleet.FLE_ID!)
  },
)

watch(
  () => fleetState.value.logBookItems,
  () => {
    nextTick(() => {
      logbook.value.scrollTop = logbook.value.scrollHeight
    })
  },
)
</script>
<style scoped>
table {
  width: 100%;
  display: table;
}

.logbook {
  font-size: 12px;
}

tbody {
  height: 25em; /* 5 times the equivalent of a text "size". */
  overflow-y: scroll;
}
</style>
